import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const ATTRIBUTE_KEY = 'Bake Day';
const BAKE_WEEKDAYS = [2, 5]; // Tuesday, Friday (0 = Sunday)
// Products that can only be baked on Tuesdays. When one of these handles is
// in the cart, Friday is removed from the bake-day picker entirely.
const TUESDAY_ONLY_PRODUCT_HANDLES = ['sourdough-country-loaf'];
const TUESDAY_ONLY_WEEKDAYS = [2];
const CUTOFF_MINUTES = 9 * 60; // 9:00am
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

class BakeDaySelector extends Component {
  // Effective set of allowed bake weekdays for the current cart contents.
  // Recomputed from the live cart any time the selector (re)renders.
  restrictedWeekdays = BAKE_WEEKDAYS;

  connectedCallback() {
    super.connectedCallback?.();
    this.timezone = this.dataset.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Always start with no pre-selection — customer must actively choose a date.
    this.currentValue = '';
    this.#applyBakeDayRestriction();

    // DialogOpenEvent does not bubble, so it must be caught in the capture
    // phase to be observed from `document`.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleExternalRefresh);
    document.addEventListener(DialogOpenEvent.eventName, this.#handleExternalRefresh, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleExternalRefresh);
    document.removeEventListener(DialogOpenEvent.eventName, this.#handleExternalRefresh, { capture: true });
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#refresh();
  }

  /**
   * CartUpdateEvent fires synchronously before morphSection rewrites the DOM.
   * Deferring to requestAnimationFrame ensures we re-render options after the
   * morph has replaced the fieldset with the server-rendered placeholder.
   */
  #handleExternalRefresh = () => {
    requestAnimationFrame(() => this.#refresh());
  };

  #refresh() {
    // Keep whatever the customer selected this session; never read data-current
    // from the server (which would cause pre-selection on page load).
    this.#applyBakeDayRestriction();
    // morphSection re-disables checkout via server HTML; re-enable it if the
    // customer already chose a date in this session.
    if (this.currentValue) {
      document.querySelectorAll('[name="checkout"]').forEach((btn) => btn.removeAttribute('disabled'));
      document.querySelectorAll('.cart__bake-day-required-note').forEach((note) => note.remove());
    }
  }

  /**
   * Reads the live cart and restricts the available bake weekdays to
   * Tuesday-only when a Tuesday-only product (e.g. Sourdough Country Loaf)
   * is present. Falls back to the full set of bake days if the cart can't
   * be read, then re-renders the options with the resolved restriction.
   */
  async #applyBakeDayRestriction() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) throw new Error('Failed to load cart');
      const cart = await response.json();
      const hasTuesdayOnlyItem = cart.items.some((item) => TUESDAY_ONLY_PRODUCT_HANDLES.includes(item.handle));
      this.restrictedWeekdays = hasTuesdayOnlyItem ? TUESDAY_ONLY_WEEKDAYS : BAKE_WEEKDAYS;
    } catch (error) {
      this.restrictedWeekdays = BAKE_WEEKDAYS;
    }
    this.renderOptions();
  }

  getZonedNow() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const map = {};
    for (const part of parts) map[part.type] = part.value;
    return {
      year: parseInt(map.year, 10), month: parseInt(map.month, 10), day: parseInt(map.day, 10),
      hour: map.hour === '24' ? 0 : parseInt(map.hour, 10), minute: parseInt(map.minute, 10),
    };
  }

  getAvailableBakeDays(count = 2) {
    const now = this.getZonedNow();
    const anchor = Date.UTC(now.year, now.month - 1, now.day);
    const nowMinutes = now.hour * 60 + now.minute;
    const results = [];
    for (let i = 1; i < 30 && results.length < count; i++) {
      const candidate = new Date(anchor + i * 86400000);
      const weekday = candidate.getUTCDay();
      if (!this.restrictedWeekdays.includes(weekday)) continue;
      const cutoffOffsetDays = i - 1;
      const available = cutoffOffsetDays > 0 || (cutoffOffsetDays === 0 && nowMinutes < CUTOFF_MINUTES);
      if (!available) continue;
      const label = `${WEEKDAY_NAMES[weekday]}, ${MONTH_NAMES[candidate.getUTCMonth()]} ${candidate.getUTCDate()}`;
      const value = `${WEEKDAY_NAMES[weekday]}, ${candidate.getUTCFullYear()}-${String(candidate.getUTCMonth() + 1).padStart(2, '0')}-${String(candidate.getUTCDate()).padStart(2, '0')}`;
      results.push({ label, value });
    }
    return results;
  }

  /**
   * If the very next occurrence of a bake weekday has already passed today's
   * 9am cutoff, returns its weekday name and date label so the UI can prompt
   * customers to call and check for leftover stock. Only the immediate next
   * day can ever be in this state — every bake day after that is always
   * still open (see getAvailableBakeDays' cutoffOffsetDays check), so no
   * loop is needed here.
   */
  getMissedBakeDay() {
    const now = this.getZonedNow();
    const anchor = Date.UTC(now.year, now.month - 1, now.day);
    const nowMinutes = now.hour * 60 + now.minute;
    const candidate = new Date(anchor + 86400000);
    const weekday = candidate.getUTCDay();
    if (!this.restrictedWeekdays.includes(weekday) || nowMinutes < CUTOFF_MINUTES) return null;
    return {
      weekday: WEEKDAY_NAMES[weekday],
      label: `${MONTH_NAMES[candidate.getUTCMonth()]} ${candidate.getUTCDate()}`,
    };
  }

  renderOptions() {
    const options = this.getAvailableBakeDays(2);
    this.#renderMissedNotice(this.getMissedBakeDay());
    const fieldset = this.querySelector('[ref="bakeDayOptions"]');
    if (!fieldset) return;

    const existingInputs = fieldset.querySelectorAll('input[name="bake-day"]');
    const alreadyRendered =
      existingInputs.length === options.length &&
      Array.from(existingInputs).every((input, index) => input.value === options[index]?.value);

    if (alreadyRendered) {
      // Just keep the checked state in sync without rebuilding the DOM.
      existingInputs.forEach((input) => {
        input.checked = this.currentValue === input.value;
      });
      return;
    }

    fieldset.innerHTML = '';
    for (const option of options) {
      const id = `bake-day-${option.value.replace(/[^a-zA-Z0-9]/g, '-')}`;
      const wrapper = document.createElement('label');
      wrapper.className = 'bake-day-selector__option';
      wrapper.setAttribute('for', id);
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'bake-day';
      input.id = id;
      input.value = option.value;
      input.checked = this.currentValue === option.value;
      input.addEventListener('change', () => this.selectBakeDay(option.value));
      const text = document.createElement('span');
      text.textContent = option.label;
      wrapper.appendChild(input);
      wrapper.appendChild(text);
      fieldset.appendChild(wrapper);
    }
    if (options.length === 0) {
      fieldset.innerHTML = '<p>No upcoming bake days are available right now. Please check back soon.</p>';
    }
  }

  #renderMissedNotice(missed) {
    const notice = this.querySelector('[ref="bakeDayMissedNotice"]');
    if (!notice) return;
    if (missed) {
      notice.textContent = `Missed ${missed.weekday}'s cut-off for ${missed.label}? We sometimes have spare stock — call us on 07510 537803 to check.`;
      notice.hidden = false;
    } else {
      notice.textContent = '';
      notice.hidden = true;
    }
  }

  async selectBakeDay(value) {
    this.currentValue = value;
    this.setAttribute('data-current', value);
    const status = this.querySelector('[ref="bakeDayStatus"]');
    if (status) status.textContent = 'Saving...';
    try {
      const config = fetchConfig('json', { body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: value } }) });
      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error('Failed to update cart');
      if (status) status.textContent = `Bake day set to ${value.split(', ')[0]}.`;
      document.querySelectorAll('[name="checkout"]').forEach((button) => { button.removeAttribute('disabled'); });
      document.querySelectorAll('.cart__bake-day-required-note').forEach((note) => note.remove());
      document.querySelectorAll('bake-day-selector').forEach((element) => {
        if (element !== this) {
          element.setAttribute('data-current', value);
          if (typeof element.refresh === 'function') element.refresh();
        }
      });
    } catch (error) {
      if (status) status.textContent = 'Something went wrong saving your bake day. Please try again.';
    }
  }

  refresh() {
    this.#refresh();
  }
}

if (!customElements.get('bake-day-selector')) {
  customElements.define('bake-day-selector', BakeDaySelector);
}
